import React from 'react';
import CollapsibleInfo from '../components/CollapsibleInfo';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../../shared/AppSelect';

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const AddressStep = ({ data, errors, onChange, onTouch }) => {
  const handleChange = (field, value) => {
    onChange(field, value);
    onTouch(field);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Company Address</h3>
        <p className="text-gray-600">Enter your company's registered business address</p>
      </div>

      <div className="space-y-4">
        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Complete Address
          </label>
          <textarea
            rows={4}
            value={data.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[100px] ${
              errors.address ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter complete business address including building, street, area"
            style={{ minHeight: '100px' }}
          />
          {errors.address && (
            <p className="mt-1 text-sm text-red-600">{errors.address}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              value={data.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter city name"
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-600">{errors.city}</p>
            )}
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State
            </label>
            <AppSelect
              value={data.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder="Select state"
              aria-label="State"
              sheetTitle="State"
              options={[
                { value: '', label: 'Select state' },
                ...indianStates.map((state) => ({ value: state, label: state })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} px-4 py-3 rounded-lg ${
                errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.state && (
              <p className="mt-1 text-sm text-red-600">{errors.state}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              value={data.country || 'India'}
              onChange={(e) => handleChange('country', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
              placeholder="India"
              readOnly
            />
            <p className="mt-1 text-sm text-gray-500">
              Currently only India is supported
            </p>
          </div>

          {/* Pincode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pincode
            </label>
            <input
              type="text"
              value={data.pincode || ''}
              onChange={(e) => handleChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.pincode ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter 6-digit pincode"
              maxLength={6}
            />
            {errors.pincode && (
              <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>
            )}
          </div>
        </div>
      </div>


      {/* Collapsible Info Box */}
      <CollapsibleInfo title="Address Guidelines">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Complete Address:</strong> Include building number, street name, and area</li>
          <li>• <strong>City & State:</strong> Ensure correct spelling for GST compliance</li>
          <li>• <strong>PIN Code:</strong> 6-digit Indian postal code for your location</li>
          <li>• This address will appear on all invoices, bills, and legal documents</li>
          <li>• Make sure the address matches your business registration documents</li>
        </ul>
      </CollapsibleInfo>
    </div>
  );
};

export default AddressStep;
